/**
 * Proposals List Component
 * Displays saved portfolio proposals with status and actions
 */

import React, { useState, useEffect } from 'react';
import { FileText, Eye, CheckCircle, XCircle, Clock, ChevronRight, ShieldCheck, AlertTriangle } from 'lucide-react';
import { portfolioApiService } from '../../services/api/portfolioApiService';
import { analyzeProposal, type AnalysisResponse } from '../../services/complianceActivityService';
import { FEATURES } from '../../config/api-config';
import { formatCurrency, formatDateTime } from '../../utils/formatters';

interface PortfolioSummary {
  total_value: number;
  health_score?: number;
  positions_count?: number;
}

interface Transaction {
  type: 'buy' | 'sell' | 'add_cash';
  symbol?: string;
  quantity?: number;
  price?: number;
  amount?: number;
  currency: string;
  source?: string; // Source/comment for cash transactions
}

interface Proposal {
  id: string;
  proposal_name: string;
  proposal_type: string;
  status: 'draft' | 'pending_review' | 'approved' | 'rejected' | 'implemented';
  created_at: string;
  reviewed_at?: string;
  current_portfolio?: PortfolioSummary;
  proposed_portfolio?: PortfolioSummary;
  transactions?: Transaction[];
  rationale?: string;
  advisor_notes?: string;
  total_commission?: number;
  total_stamp_duty?: number;
  total_fees?: number;
}

interface ProposalsListProps {
  clientId: string;
  onSelectProposal?: (proposal: Proposal) => void;
  refreshTrigger?: number; // Add a trigger to force refresh
}

const ProposalsList: React.FC<ProposalsListProps> = ({ clientId, onSelectProposal, refreshTrigger }) => {
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [expandedProposal, setExpandedProposal] = useState<string | null>(null);
  // TASK-517: FinSA/MiFID suitability results keyed by proposal id
  const [suitability, setSuitability] = useState<Record<string, AnalysisResponse>>({});
  const [suitabilityLoading, setSuitabilityLoading] = useState<string | null>(null);
  const [suitabilityError, setSuitabilityError] = useState<Record<string, string>>({});

  const checkRequiredForms = async (proposalId: string) => {
    setSuitabilityLoading(proposalId);
    setSuitabilityError((prev) => ({ ...prev, [proposalId]: '' }));
    try {
      const result = await analyzeProposal(proposalId, clientId);
      setSuitability((prev) => ({ ...prev, [proposalId]: result }));
    } catch (error) {
      console.error('Suitability analysis failed:', error);
      setSuitabilityError((prev) => ({ ...prev, [proposalId]: 'Analysis failed. Please try again.' }));
    } finally {
      setSuitabilityLoading(null);
    }
  };

  useEffect(() => {
    if (clientId) {
      loadProposals();
    }
  }, [clientId, selectedStatus, refreshTrigger]); // Add refreshTrigger to dependencies

  const loadProposals = async () => {
    setLoading(true);
    try {
      const status = selectedStatus === 'all' ? undefined : selectedStatus;
      const response = await portfolioApiService.getProposals(clientId, status);
      setProposals(response.proposals || []);
    } catch (error) {
      console.error('Failed to load proposals:', error);
      setProposals([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'rejected':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'pending_review':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      case 'implemented':
        return <CheckCircle className="w-4 h-4 text-blue-600" />;
      default:
        return <FileText className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'pending_review':
        return 'bg-yellow-100 text-yellow-800';
      case 'implemented':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Using formatters from shared utility

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-4 border-b">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Portfolio Proposals
          </h3>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-3 py-1 border rounded-lg text-sm"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="pending_review">Pending Review</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="implemented">Implemented</option>
          </select>
        </div>
      </div>

      <div className="divide-y">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading proposals...</div>
        ) : proposals.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No proposals found</div>
        ) : (
          proposals.map((proposal) => (
            <div key={proposal.id} className="p-4 hover:bg-gray-50">
              <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setExpandedProposal(
                  expandedProposal === proposal.id ? null : proposal.id
                )}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    {getStatusIcon(proposal.status)}
                    <h4 className="font-medium">{proposal.proposal_name}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(proposal.status)}`}>
                      {proposal.status.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    Created: {formatDateTime(proposal.created_at)}
                    {proposal.transactions && proposal.transactions.length > 0 && (
                      <span className="ml-3">• {proposal.transactions.length} transactions</span>
                    )}
                    {proposal.total_fees !== undefined && proposal.total_fees > 0 && (
                      <span className="ml-3 text-red-600">• Fees: {formatCurrency(proposal.total_fees)}</span>
                    )}
                  </div>
                </div>
                <ChevronRight 
                  className={`w-5 h-5 text-gray-400 transition-transform ${
                    expandedProposal === proposal.id ? 'rotate-90' : ''
                  }`}
                />
              </div>

              {expandedProposal === proposal.id && (
                <div className="mt-4 pl-7 space-y-3">
                  {/* Value Comparison */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-xs text-gray-600 mb-1">Current Value</p>
                      <p className="font-semibold">
                        {formatCurrency(proposal.current_portfolio?.total_value || 0)}
                      </p>
                    </div>
                    <div className="bg-blue-50 p-3 rounded">
                      <p className="text-xs text-gray-600 mb-1">Proposed Value</p>
                      <p className="font-semibold">
                        {formatCurrency(proposal.proposed_portfolio?.total_value || 0)}
                      </p>
                    </div>
                  </div>

                  {/* Transaction Fees */}
                  {(proposal.total_fees !== undefined && proposal.total_fees > 0) && (
                    <div className="bg-yellow-50 p-3 rounded">
                      <p className="text-sm font-medium text-gray-700 mb-2">Transaction Fees:</p>
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <div>
                          <p className="text-xs text-gray-600">Commission</p>
                          <p className="font-semibold">{formatCurrency(proposal.total_commission || 0)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">Stamp Duty</p>
                          <p className="font-semibold">{formatCurrency(proposal.total_stamp_duty || 0)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">Total Fees</p>
                          <p className="font-semibold text-red-600">{formatCurrency(proposal.total_fees || 0)}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Rationale */}
                  {proposal.rationale && (
                    <div>
                      <p className="text-sm font-medium text-gray-700">Rationale:</p>
                      <p className="text-sm text-gray-600">{proposal.rationale}</p>
                    </div>
                  )}

                  {/* Transactions */}
                  {proposal.transactions && proposal.transactions.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Transactions:</p>
                      <div className="space-y-1">
                        {proposal.transactions.map((tx, idx) => (
                          <div key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                            <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                              tx.type === 'buy' ? 'bg-green-500' :
                              tx.type === 'sell' ? 'bg-red-500' : 'bg-blue-500'
                            }`} />
                            <div className="flex-1">
                              {tx.type === 'add_cash' ? (
                                <>
                                  <div>{`Add ${tx.amount} ${tx.currency}`}</div>
                                  {tx.source && (
                                    <div className="text-xs text-gray-500 italic mt-0.5">
                                      Source: {tx.source}
                                    </div>
                                  )}
                                </>
                              ) : (
                                `${tx.type.toUpperCase()} ${tx.quantity} ${tx.symbol} @ ${tx.price}`
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={() => onSelectProposal && onSelectProposal(proposal)}
                      className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 flex items-center gap-1"
                    >
                      <Eye className="w-3 h-3" />
                      View Details
                    </button>
                    {FEATURES.ENABLE_FINSA_COMPLIANCE && (
                      <button
                        onClick={() => checkRequiredForms(proposal.id)}
                        disabled={suitabilityLoading === proposal.id}
                        className="px-3 py-1 bg-emerald-600 text-white rounded text-sm hover:bg-emerald-700 disabled:opacity-60 flex items-center gap-1"
                      >
                        <ShieldCheck className="w-3 h-3" />
                        {suitabilityLoading === proposal.id ? 'Checking…' : 'Check required forms'}
                      </button>
                    )}
                  </div>

                  {/* TASK-517: FinSA/MiFID suitability result */}
                  {suitabilityError[proposal.id] && (
                    <div className="mt-2 text-sm text-red-600">{suitabilityError[proposal.id]}</div>
                  )}
                  {suitability[proposal.id] && (() => {
                    // TASK-579: derive an aligned/breach verdict from the real engine output.
                    const forms = suitability[proposal.id].required_forms;
                    const breaches = forms.filter((f) => f.form_id === 'risk-profile-override' || f.form_id === 'risk-profile-required');
                    const missing = breaches.some((f) => f.form_id === 'risk-profile-required');
                    const aligned = suitability[proposal.id].is_compliant && breaches.length === 0;
                    return (
                    <div className={`mt-3 p-3 border rounded ${aligned ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                      <div className={`flex items-center gap-2 mb-2 text-sm font-semibold ${aligned ? 'text-emerald-700' : 'text-red-700'}`}>
                        {aligned ? <ShieldCheck className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                        {missing ? 'Profile missing — suitability cannot be assessed' : aligned ? 'Suitable for risk profile' : 'Suitability breach'}
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-800">
                          Required forms ({suitability[proposal.id].required_forms.length})
                        </span>
                        <span className="text-xs text-gray-600">
                          Compliance score: {(suitability[proposal.id].compliance_score * 100).toFixed(0)}%
                          {suitability[proposal.id].is_compliant ? ' ✓' : ''}
                        </span>
                      </div>
                      {suitability[proposal.id].required_forms.length === 0 ? (
                        <div className="text-sm text-gray-500">No suitability forms required.</div>
                      ) : (
                        <ul className="space-y-1">
                          {suitability[proposal.id].required_forms.map((form) => (
                            <li key={form.form_id} className="text-sm text-gray-700 flex items-center gap-2">
                              <span className="font-medium">{form.form_name}</span>
                              <span className="text-xs text-gray-500">
                                {form.regulation} · {form.status}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                      {suitability[proposal.id].warnings.map((w, i) => (
                        <div key={i} className="mt-1 text-xs text-amber-700 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          {w}
                        </div>
                      ))}
                    </div>
                    );
                  })()}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ProposalsList;